// <emma-chat-result-header> — header inside .chat-bubble--emma above the
// results-peek (DS chats.html canonical assembly). Two visual states lifted
// 1:1 from DS result-header.html / result-header.css:
//
//   mode="scanning" → .result-intro with Braille spinner ⠿ + animated ⠁
//                     "Scanning N configurations across providers…"
//   mode="found"    → .step.step--found with checkmark SVG + count + meta
//                     "Found N best deals · M scanned · K providers"
//
// The Braille spinner uses ::before/::after with @keyframes content cycling
// — the only place in the widget that relies on `content` animation. It's
// CSS-only (no JS timer), so it survives prefers-reduced-motion correctly
// when the keyframes fall back to the default ⠁ frame.

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { designTokens } from './design-tokens.js';

export type ResultHeaderMode = 'scanning' | 'found';

@customElement('emma-chat-result-header')
export class EmmaChatResultHeader extends LitElement {
  /** 'scanning' shows the Braille spinner intro; 'found' shows the checkmark step. */
  @property({ type: String })
  mode: ResultHeaderMode = 'scanning';

  /** Result count (found state) or scanned count (scanning state). */
  @property({ type: Number })
  count = 0;

  /** Total configurations scanned across providers. Shown in step-meta in found state. */
  @property({ type: Number })
  scanned = 0;

  /** Number of distinct providers in results. Shown in step-meta in found state. */
  @property({ type: Number, attribute: 'provider-count' })
  providerCount = 0;

  /** Override summary text for the found state. When omitted, builds from count. */
  @property({ type: String })
  summary = '';

  /** Override the scanning-state lead text. Used by <emma-chat-turn>'s
   *  reveal timeline to morph from "Scanning N configurations across
   *  providers…" → "Comparing pricing across regions…" without changing
   *  the spinner. When empty, falls back to the default "Scanning…" text. */
  @property({ type: String, attribute: 'scanning-text' })
  scanningText = '';

  /** Relative time label for the meta line (e.g. "just now", "2 min ago").
   *  Named `updatedLabel` rather than `updated` to avoid the collision
   *  with Lit's `updated()` lifecycle method. Defaults to "just now" —
   *  host can override with a live-updating string. */
  @property({ type: String, attribute: 'updated-label' })
  updatedLabel = 'just now';

  static override styles = [
    designTokens,
    // Lifted 1:1 from design-system/components/result-header.css.
    css`
      :host {
        display: block;
      }

      .result-header {
        margin-bottom: var(--space-3);
      }

      /* ---- Scanning state ---- */
      .result-intro {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: var(--text-sm);
        line-height: var(--leading-none);
        color: var(--gray-60);
        margin-bottom: var(--space-2);
      }
      /* Braille spinner: gray track ⠿ + blue dot cycling clockwise. */
      .result-intro-spinner {
        position: relative;
        display: inline-block;
        flex-shrink: 0;
        font-family: monospace;
        font-size: 1em;
        line-height: 1;
      }
      .result-intro-spinner::before {
        content: '⠿';
        color: var(--gray-15);
      }
      .result-intro-spinner::after {
        content: '⠁';
        position: absolute;
        left: 0;
        top: 0;
        color: var(--blue-60);
        animation: braille-spin 0.9s infinite;
      }
      /* Clockwise dot cycle. */
      @keyframes braille-spin {
        0% {
          content: '⠁';
        }
        16.7% {
          content: '⠈';
        }
        33.3% {
          content: '⠐';
        }
        50% {
          content: '⠠';
        }
        66.7% {
          content: '⠄';
        }
        83.3% {
          content: '⠂';
        }
        100% {
          content: '⠁';
        }
      }

      /* ---- Found state ---- */
      .step {
        display: flex;
        align-items: center;
        gap: 7px;
        font-size: var(--text-sm);
        line-height: var(--leading-none);
        color: var(--gray-60);
      }
      .step--found {
        color: var(--color-text);
        font-weight: var(--font-semibold);
      }
      .step-check {
        width: 1em;
        height: 1em;
        color: var(--color-success);
        flex-shrink: 0;
      }
      .step--found .step-check {
        animation: step-check-arrive var(--duration-arrival) var(--easing-spring) both;
      }
      @keyframes step-check-arrive {
        from {
          opacity: 0;
          transform: scale(0.4);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      .step-meta {
        font-size: var(--text-sm);
        font-weight: var(--font-regular);
        color: var(--gray-40);
        margin-left: 4px;
      }

      @media (prefers-reduced-motion: reduce) {
        .result-intro-spinner::after,
        .step--found .step-check {
          animation: none;
        }
      }
    `,
  ];

  override render() {
    if (this.mode === 'scanning') {
      const text =
        this.scanningText ||
        (this.count > 0
          ? `Scanning ${this.count.toLocaleString()} configurations across providers…`
          : 'Scanning configurations across providers…');
      return html`
        <div class="result-header">
          <div class="result-intro">
            <span class="result-intro-spinner" aria-hidden="true"></span>
            ${text}
          </div>
        </div>
      `;
    }

    const headline =
      this.summary ||
      (this.count === 1 ? 'Found 1 result' : `Found ${this.count} best deals`);
    const metaParts: string[] = [];
    // Meta-line format mirrors monolith :3473-3476 — "{N} scanned ·
    // across {K} providers · updated {time}". Each segment is optional.
    if (this.scanned > 0) metaParts.push(`${this.scanned.toLocaleString()} scanned`);
    if (this.providerCount > 0) {
      // Always "across N providers" — monolith :3475 doesn't pluralise.
      metaParts.push(`across ${this.providerCount} providers`);
    }
    if (this.updatedLabel) metaParts.push(`updated ${this.updatedLabel}`);
    const meta = metaParts.length ? metaParts.join('  ·  ') : '';

    return html`
      <div class="result-header">
        <div class="step step--found">
          <svg class="step-check" viewBox="0 0 10 10" fill="none">
            <path
              d="M1.5 5l2.5 2.5 4.5-4.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          ${headline} ${meta ? html`<span class="step-meta">${meta}</span>` : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-result-header': EmmaChatResultHeader;
  }
}
